import { useEffect, useMemo, useRef, useState } from "react"

import { AnimatePresence, motion } from "motion/react"
import { useTranslation } from "react-i18next"

import type { GroupedActivityMessage } from "../../types/activity-message-types"

import { Check, ChevronLeft, ChevronRight } from "@/components/icons"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

type AgentSidebarProps = {
  groupedMessages: GroupedActivityMessage[]
  activeSection: number
  isLoading: boolean
}

export function ActiveAgents({ groupedMessages, activeSection, isLoading }: AgentSidebarProps) {
  const { t } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Filter groups based on active section index (current depth being displayed)
  const activeGroups = groupedMessages.filter((_, index) => index === activeSection)

  // Extract all agents from active groups (already processed and deduplicated in groupActivityMessages)
  const allAgents = useMemo(() => {
    return activeGroups.flatMap((group) => group.agents)
  }, [activeGroups])

  // Keep the fallback for backward compatibility
  const isGlobalLoading = isLoading && activeSection === groupedMessages.length - 1

  // Check scroll capabilities
  const checkScrollCapabilities = () => {
    const container = scrollContainerRef.current
    if (!container) return

    setCanScrollLeft(container.scrollLeft > 0)
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth)
  }

  // Scroll functions
  const scrollLeft = () => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollBy({ left: -200, behavior: "smooth" })
  }

  const scrollRight = () => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollBy({ left: 200, behavior: "smooth" })
  }

  // Effect to check scroll capabilities on mount and when agents change
  useEffect(() => {
    checkScrollCapabilities()
  }, [allAgents])

  // Handle scroll events
  const handleScroll = () => {
    checkScrollCapabilities()
  }

  if (allAgents.length === 0) {
    return (
      <section className="h-20">
        <div
          className={cn(
            "from-background to-muted/10 flex h-full min-w-[175px] items-center justify-center gap-1 overflow-hidden rounded-xl bg-gradient-to-br p-4 shadow-sm"
          )}
        >
          <span
            className={cn(
              "text-muted-foreground text-center text-sm leading-5 font-bold text-balance"
            )}
          >
            {t("activity_stats.no_active_agents")}
          </span>
        </div>
      </section>
    )
  }

  return (
    <section className="relative">
      {/* Left scroll button */}
      {canScrollLeft && (
        <button
          aria-label="Scroll left"
          className={cn(
            "bg-background/80 hover:bg-background absolute top-1/2 left-6 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full shadow-md backdrop-blur-sm transition-[background-color,box-shadow] hover:shadow-lg",
            "border-border/50 border",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
          )}
          onClick={scrollLeft}
        >
          <ChevronLeft className="text-foreground/70 h-4 w-4" />
        </button>
      )}

      {/* Right scroll button */}
      {canScrollRight && (
        <button
          aria-label="Scroll right"
          className={cn(
            "bg-background/80 hover:bg-background absolute top-1/2 right-6 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full shadow-md backdrop-blur-sm transition-[background-color,box-shadow] hover:shadow-lg",
            "border-border/50 border",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
          )}
          onClick={scrollRight}
        >
          <ChevronRight className="text-foreground/70 h-4 w-4" />
        </button>
      )}

      {/* Scrollable container */}
      <div
        ref={scrollContainerRef}
        className="hide-scrollbar grid h-20 auto-cols-[175px] grid-flow-col gap-4 overflow-x-auto pb-2"
        onScroll={handleScroll}
      >
        <AnimatePresence mode="popLayout">
          {allAgents.map((agent) => {
            // Use agent-specific isLoading if available, otherwise fall back to global loading
            const agentIsLoading = isLoading ? agent.isLoading : isGlobalLoading

            return (
              <motion.div
                key={agent.name}
                layout
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                className={cn(
                  "from-background to-muted/10 flex h-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-br p-4 shadow-sm"
                )}
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.1, 0.25, 1], // custom ease for smooth animation
                }}
              >
                <span
                  className={cn(
                    "text-foreground/90 text-center text-sm leading-5 font-bold text-balance"
                  )}
                >
                  {agent.name}
                </span>
                {agentIsLoading ? (
                  <Spinner className="text-muted-foreground size-3 shrink-0" />
                ) : (
                  <Check className="text-success size-3 shrink-0" />
                )}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </section>
  )
}
